import React from "react"
import type { IDashboardContextValue } from "connected-components/dashboard/context/types"
import type { InstrumentWithMetadata } from "helpers/types/instruments/metadata"
import type { TabIconComponent } from "connected-components/op-layout-engine/types"
import type { ArtifactKind } from "./ai-artifact/types"

/**
 * Module type constants
 */
export const MODULE_TYPES = {
  ASSET_VIEW: "asset-view",
  KPI: "kpi",
  GRAPH: "graph",
  FACTORS: "factors",
  AI_ARTIFACT: "ai-artifact",
} as const

export type ModuleType = typeof MODULE_TYPES[keyof typeof MODULE_TYPES]

export interface ModuleDefinition {
  type: ModuleType
  title: string
  icon?: TabIconComponent
  removable: boolean
  editable: boolean
  removeRequiresConfirmation?: boolean
  description: string
}

/**
 * Dependencies required for rendering a module
 * TODO[APP-2461]: Improve module renderer dependencies architecture
 */
export interface ModuleRenderDependencies {
  TradeContext: React.Context<IDashboardContextValue>
  instrumentsWithMetadata: Record<string, InstrumentWithMetadata>
  hasMultipleInstruments?: boolean
  contentSets?: unknown[]
  modelMetadata?: unknown
  config?: ModuleConfig
  /**
   * True when this panel should read legacy static `dashboard.asset-view` prefs as fallback
   * (sole primary asset-view, unless overridden in module config).
   */
  shouldUseLegacyAssetViewPreferences?: boolean
  /** Merges into persisted dashboard `modules` config (e.g. sync `initialView` with asset-view prefs). */
  syncDashboardModuleConfig?: (config: Partial<ModuleConfig>) => void
}

export interface ModuleConfig {
  artifactId?: string
  pinnedId?: string
  kind?: ArtifactKind
  title?: string
  isPinning?: boolean
  hasError?: boolean
  /**
   * When set, overrides automatic legacy `dashboard.asset-view` preference migration for this panel.
   * When omitted, migration runs only for the sole primary asset-view module on the dashboard.
   */
  shouldUseLegacyAssetViewPreferences?: boolean
  [key: string]: unknown
}

export interface DashboardModule {
  id: string
  type: ModuleType
  config: ModuleConfig
}

export interface ModulesState {
  modules: DashboardModule[]
}

export interface ModulesContextValue extends ModulesState {
  isPreferencesLoaded: boolean
  addModule: (module: { type: ModuleType, config?: ModuleConfig }) => string
  removeModule: (module: DashboardModule) => void
  updateModule: (moduleId: string, updates: Partial<DashboardModule>) => void
  resetModulesToDefault: () => void
}
